import { useEffect, useMemo, useState } from "react";
import { ProductsAPI } from "../api/productApi";
import ProductForm from "../components/products/ProductForm";
import Navbar from "../components/Navbar";
import ProductCard from "../components/products/ProductCard";

const EMPTY_FORM = {
  name: "",
  brand: "",
  size_label: "",
  unit_type: "",
  case_pack_qty: "",
  base_price_per_case: "",
  derived_unit_price: 0,
  category_id: "",
  tax_rate: 0,
  barcode: "",
  sku: "",
  owner_id: "",
  active: true,
  media: [],
};

export default function ProductsPage() {
  const [items, setItems] = useState([]);
  const [insights, setInsights] = useState(null);

  const [categories, setCategories] = useState([]); // optional fetch below

  const [showForm, setShowForm] = useState(false);   // <<< hidden by default
  const [editing, setEditing] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [list, meta] = await Promise.all([
      ProductsAPI.list(),
      ProductsAPI.insights().catch(() => null),
    ]);
    setItems(list || []);
    setInsights(meta);
  };

  useEffect(() => {
    load();

    // Optional: load categories if your API exposes it
    (async () => {
      try {
        if (typeof ProductsAPI.categories === "function") {
          const cats = await ProductsAPI.categories();
          if (Array.isArray(cats)) setCategories(cats);
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  // --- Actions ---
  const handleAddClick = () => {
    setEditing(false);
    setFormValues(EMPTY_FORM);
    setShowForm(true);
  };

  const onEdit = (p) => {
    // Map product -> form values (safe defaults)
    const mapped = {
      id: p.id,
      name: p.name || "",
      brand: p.brand || "",
      size_label: p.size_label || "",
      unit_type: p.unit_type || "",
      case_pack_qty: p.case_pack_qty ?? "",
      base_price_per_case: p.base_price_per_case ?? "",
      derived_unit_price:
        p.derived_unit_price ??
        (p.base_price_per_case && p.case_pack_qty
          ? Number(p.base_price_per_case) / Number(p.case_pack_qty)
          : 0),
      category_id: p.category_id ?? "",
      tax_rate: p.tax_rate ?? 0,
      barcode: p.barcode || "",
      sku: p.sku || "",
      owner_id: p.owner_id ?? "",
      active: !!p.active,
      media: (() => { try { return JSON.parse(p?.media || "[]"); } catch { return []; } })(),
    };
    setFormValues(mapped);
    setEditing(true);
    setShowForm(true); // <<< show when editing
  };

  const onDelete = async (id) => {
    if (!window.confirm("Disable this product?")) return;
    try {
      await ProductsAPI.remove(id);
      await load();
    } catch (e) {
      alert(e.message || "Failed to delete");
    }
  };

  const handleCancel = () => {
    setShowForm(false);     // <<< hide form
    setEditing(false);
    setFormValues(EMPTY_FORM);
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editing && formValues?.id) {
        values.media = JSON.stringify(values.media)
        await ProductsAPI.update(formValues.id, values);
      } else {
        await ProductsAPI.create(values);
      }
      await load();
      // reset + hide
      setEditing(false);
      setFormValues(EMPTY_FORM);
      setShowForm(false);   // <<< hide after submit
    } catch (e) {
      alert(e.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-screen-2xl mx-auto p-6">
        {/* Header / Actions */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">
            Products
          </h1>
          <div className="flex gap-2">
            {showForm ? (
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200"
              >
                Hide Form
              </button>
            ) : (
              <button
                type="button"
                onClick={handleAddClick}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700"
              >
                Add Product
              </button>
            )}
          </div>
        </div>

        {/* Insights */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-white rounded shadow">
            <div className="text-gray-500">Total Products</div>
            <div className="text-3xl font-bold">{insights?.totalProducts ?? "-"}</div>
          </div>
          <div className="p-4 bg-white rounded shadow">
            <div className="text-gray-500">Total Units</div>
            <div className="text-3xl font-bold">{insights?.totalUnits ?? 0}</div>
          </div>
          <div className="p-4 bg-white rounded shadow">
            <div className="text-gray-500">Avg Case Price</div>
            <div className="text-3xl font-bold">
              ₹{insights?.avgCasePrice?.toFixed?.(2) ?? "-"}
            </div>
          </div>
          <div className="p-4 bg-white rounded shadow">
            <div className="text-gray-500">Categories</div>
            <div className="text-sm">
              {insights?.byCategory
                ?.map((c) => `${c.category || "Uncategorized"} (${c.count})`)
                .join(", ") || "-"}
            </div>
          </div>
        </div>

        {/* Add/Edit Form - hidden by default */}
        {showForm && (
          <div className="mb-8">
            <ProductForm
              form={formValues}         // <<< initial values for Formik
              onSubmit={onSubmit}
              onCancel={handleCancel}
              editing={editing}
              submitting={submitting}
              categories={categories}
            />
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {(items || []).length > 0 ? (
            items.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          ) : (
            <div className="col-span-full text-center text-gray-500 py-12">
              No products yet. Click <span className="font-medium">Add Product</span> to create one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
